import { Dialog } from '@angular/cdk/dialog';
import { computed, inject, Injectable, NgZone } from '@angular/core';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { DataStore } from '@common/data-models/data-store/data-store';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { ListData } from '@common/data-models/service/list-data';
import { ObjectProperties } from '@common/data-models/service/object-properties';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ClipboardTypes } from '@common/enums/clipboard-types';
import { OPTION_ALL, OPTION_ANY, OPTION_BLANK } from '@common/enums/constants';
import { ObjectTypes } from '@common/enums/object-types';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { FieldMappingService } from '@common/services/field-mapping-service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LayoutState, ViewStateRecordsAlias } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { Util } from '@common/utils/util';
import { Store } from '@ngrx/store';
import { ClipboardComponent } from '@platform/components/clipboard/clipboard';
import { PlaybookSearchResults } from '@playbook/data-models/playbook-search-results/playbook-search-results';
import { AssignmentCoverageType, AssignmentRecord } from '@playbook/data-models/records/assignment-record';
import { IPlaybookService } from '@playbook/interfaces/i-playbook-service';
import { FilterDef } from '@src/suite/common/data-models/navigation/filter-def';
import { StoryRecord } from '@src/suite/common/data-models/records/story-record';
import { StoryOutputType } from '@src/suite/common/enums/enums';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { NOMDatabase } from '@src/suite/common/store/records/records.models';
import { cleanupRecord, copyDataAndChildrenTo, copyDataTo, initializeRecord } from '@src/suite/common/utils/record-utils';
import { TopicRecord } from '@src/suite/playbook/data-models/records/topic-record';
import { AppService } from '@suite/app.service';
import { TopicViewStateRecord } from '@suite/common/data-models/records/topic-viewstate-record';
import { ClipboardSourceChooserComponent } from '@suite/platform/components/clipboard/modal/clipboard-source-chooser-modal.component';
import Handlebars from 'handlebars/dist/cjs/handlebars';
import { Subject } from 'rxjs';

import { PlaybookSearchService } from './playbook-search-service';
import { TopicService } from './topic-service';

@Injectable({ providedIn: 'root' })
export class PlaybookService implements IPlaybookService {
    public basicContentService: BasicContentService = inject(BasicContentService);

    public TopicRequiredFieldDefs: Array<FieldDef> = [];
    public TopicRequiredFieldDefsDict: { [id: string]: FieldDef } = {};

    public AssignmentBaseRequiredFieldDefs: Array<FieldDef> = [];
    public AssignmentBaseRequiredFieldDefsDict: { [id: string]: FieldDef } = {};
    public AssignmentRequiredFieldDefs: Array<FieldDef> = [];
    public AssignmentRequiredFieldDefsDict: { [id: string]: FieldDef } = {};
    public AssignmentDoNotCopyFieldDefs: Array<FieldDef> = [];
    public AssignmentDoNotCopyFieldDefsDict: { [id: string]: FieldDef } = {};

    public StoryRequiredFieldDefs: Array<FieldDef> = [];
    public StoryRequiredFieldDefsDict: { [id: string]: FieldDef } = {};

    public get topicItems(): Array<StorytellingTopicRecord> {
        return this.playbookSearchResults.topics;
    }

    // hovered over assignment
    public currentlyHoveredAssignmentGuid: string = null;

    destroyed$: Subject<void> = new Subject<void>();

    _vs = computed(() => this.store.selectSignal(selectCurrentViewState)());

    languageSettings = this.store.selectSignal(selectI18N)();

    constructor(
        public appService: AppService,
        public topicService: TopicService,
        public playbookSearchService: PlaybookSearchService,
        public navigationService: NavigationService,
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public ngZone: NgZone,
        public viewStateService: LayoutState,
        public fieldMappingService: FieldMappingService,
        private dialogService: Dialog,
        private store: Store
    ) {
        AssignmentRecord.playbookService = this;
        TopicRecord.playbookService = this;
        StoryRecord.playbookService = this;
        PlaybookSearchResults.playbookService = this;

        ClipboardComponent.renderers.playbook = this.copyToClipboardRenderer.bind(this);

        this.globalTablesService.staticPropertyHandlers.TOPIC = this.getTopicStaticProperties.bind(this);
        this.globalTablesService.staticPropertyHandlers.ASSIGNMENT = this.getAssignmentStaticProperties.bind(this);
        this.globalTablesService.staticPropertyHandlers.SCRIPT = this.getStoryStaticProperties.bind(this);

        this.globalTablesService.isPropertyForType[ObjectTypes.TOPIC] = this.isTopicProperty.bind(this);
        this.globalTablesService.isPropertyForType[ObjectTypes.ASSIGNMENT] = this.isAssignmentProperty.bind(this);
        this.globalTablesService.isPropertyForType[ObjectTypes.SCRIPT] = this.isScriptProperty.bind(this);

        this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.TOPIC] = this.populateDefaultValuesForTopic.bind(this);
        this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.ASSIGNMENT] = this.populateDefaultValuesForAssignment.bind(this);

        this.globalTablesService.populateCopyFromParentCacheCallbacks[ObjectTypes.ASSIGNMENT] = this.populateCopyFromParentCacheForAssignment.bind(this);

        this.globalTablesService.populateExtraFiltersCallback = this.getRelationshipFilters.bind(this);

        this.globalTablesService.setFieldDefCallbacks[ObjectTypes.TOPIC] = this.setFieldDefsForTopics.bind(this);
        this.globalTablesService.setFieldDefCallbacks[ObjectTypes.SCRIPT] = this.setFieldDefsForStories.bind(this);
        this.globalTablesService.setFieldDefCallbacks[ObjectTypes.ASSIGNMENT] = this.setFieldDefsForAssignments.bind(this);

        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.SEARCH] = this.getFiltersForSearch().bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.TOPIC] = this.getFiltersForTopicOrAssignmentOrScript(ViewStateTypes.TOPIC).bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.ASSIGNMENT] = this.getFiltersForTopicOrAssignmentOrScript(ViewStateTypes.ASSIGNMENT).bind(this);
        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.SCRIPT] = this.getFiltersForTopicOrAssignmentOrScript(ViewStateTypes.SCRIPT).bind(this);
    }

    public AssignmentBaseRequiredFields: Array<string> = [
        PSTFieldNames.PSTAssignmentOwner,
        PSTFieldNames.Title,
        PSTFieldNames.Description,
        PSTFieldNames.DescriptionType,
        PSTFieldNames.PSTDesk,
        PSTFieldNames.PSTAssignmentStatus,
    ];

    public TopicBaseRequiredFields: Array<string> = [
        PSTFieldNames.Title,
        PSTFieldNames.Description,
        PSTFieldNames.PSTDesk,
        PSTFieldNames.PSTTags,
        PSTFieldNames.PSTTopicStatus,
        PSTFieldNames.PSTTopicType,
        PSTFieldNames.PSTTopicStoryOwner,
        PSTFieldNames.Owner,
        PSTFieldNames.PSTApproved,
    ];

    public StoryBaseRequiredFields: Array<string> = [
        PSTFieldNames.Title,
        PSTFieldNames.PSTDesk,
        PSTFieldNames.PSTTags,
        PSTFieldNames.PSTStoryStatus,
        PSTFieldNames.PSTStoryOutputType,
        PSTFieldNames.PSTStoryOwner,
        PSTFieldNames.Owner,
        PSTFieldNames.PSTApproved,
    ];

    public AssignmentRequiredFields: Array<string> = [];

    public getTopicStaticProperties(): { [id: string]: boolean } {
        const ret: { [id: string]: boolean } = {};
        ret[PSTFieldNames.Title] = true;
        ret[PSTFieldNames.Description] = true;
        ret[PSTFieldNames.PSTDesk] = true;
        ret[PSTFieldNames.PSTTags] = true;
        ret[PSTFieldNames.PSTTopicStatus] = true;
        ret[PSTFieldNames.PSTTopicType] = true;
        ret[PSTFieldNames.PSTTopicStoryOwner] = true;
        ret[PSTFieldNames.Owner] = true;
        ret[PSTFieldNames.PSTApproved] = true;
        ret[PSTFieldNames.PSTTopicOrder] = true;
        return ret;
    }

    public getAssignmentStaticProperties(): { [id: string]: boolean } {
        const ret: { [id: string]: boolean } = {};
        // ret[PSTFieldNames.PSTAssignmentOwner] = true;
        ret[PSTFieldNames.Title] = true;
        ret[PSTFieldNames.PSTDesk] = true;
        ret[PSTFieldNames.DescriptionType] = true;
        ret[PSTFieldNames.PSTAssignmentCost] = true;
        ret[PSTFieldNames.PSTTags] = true;
        ret[PSTFieldNames.PSTAssignmentStatus] = true;
        ret[PSTFieldNames.Description] = true;
        ret[PSTFieldNames.PSTApproved] = true;
        return ret;
    }

    public getStoryStaticProperties(): { [id: string]: boolean } {
        const ret: { [id: string]: boolean } = {};
        ret[PSTFieldNames.Title] = true;
        ret[PSTFieldNames.PSTDesk] = true;
        ret[PSTFieldNames.PSTTags] = true;
        ret[PSTFieldNames.PSTStoryStatus] = true;
        ret[PSTFieldNames.PSTStoryOutputType] = true;
        ret[PSTFieldNames.PSTStoryOwner] = true;
        ret[PSTFieldNames.PSTApproved] = true;
        ret[PSTFieldNames.PSTStoryOrder] = true;
        return ret;
    }

    public isTopicProperty(fieldDef: FieldDef): boolean {
        return fieldDef.TopicProp;
    }

    public isStoryProperty(fieldDef: FieldDef): boolean {
        return fieldDef.StoryProp;
    }

    public isAssignmentProperty(fieldDef: FieldDef): boolean {
        return fieldDef.AssignmentProp;
    }

    public isScriptProperty(fieldDef: FieldDef): boolean {
        return fieldDef.Type?.indexOf('Script') > -1 || fieldDef.StoryProp || false;
    }

    public populateDefaultValuesForTopic(f: FieldDef, criteriaObj: any) {
        if (f.TopicProp) {
            if (!this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC]) {
                this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC] = {};
            }
            this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC][f.ID] = criteriaObj;
        }
    }

    public populateDefaultValuesForStory(f: FieldDef, criteriaObj: any) {
        if (f.StoryProp) {
            if (!this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.SCRIPT]) {
                this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.SCRIPT] = {};
            }
            this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.SCRIPT][f.ID] = criteriaObj;
        }
    }

    public populateDefaultValuesForAssignment(f: FieldDef, criteriaObj: any) {
        if (f.AssignmentProp) {
            if (!this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT]) {
                this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT] = {};
            }
            this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT][f.ID] = criteriaObj;
        }
    }

    public populateCopyFromParentCacheForAssignment(f: FieldDef) {
        if (f.CopyToChild && ((f.AssignmentProp && f.TopicProp) || f.Type === 'System')) {
            const criteriaObj = {
                key: f.ID,
                value: '',
                validation: f.Validation.toUpperCase(),
            };
            if (!this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT]) {
                this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT] = {};
            }
            this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT][f.ID] = criteriaObj;
        }
    }

    public setFieldDefsForTopics(fieldDef: FieldDef) {
        const validation = fieldDef.Validation.toUpperCase();
        if (fieldDef.Required) {
            if (validation === 'CHECKBOX' || (validation === 'LOOKUPLIST' && fieldDef.ID !== PSTFieldNames.PSTTags) || !this.globalTablesService.isValidationTypeImplemented(validation)) {
                // Required could not be applied to checkbox and unimplemented validation types
                return;
            }
            if (fieldDef.TopicProp || this.TopicBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                this.TopicRequiredFieldDefs.push(fieldDef);
                this.TopicRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
            }
        }
        // if (fieldDef.TopicProp) {
        //     this.calculateTopicPropComparators(fieldDef);
        // }
    }

    public setFieldDefsForAssignments(fieldDef: FieldDef) {
        const validation = fieldDef.Validation.toUpperCase();
        if (fieldDef.Required) {
            if (validation === 'CHECKBOX' || (validation === 'LOOKUPLIST' && fieldDef.ID !== PSTFieldNames.PSTTags) || !this.globalTablesService.isValidationTypeImplemented(validation)) {
                // Required could not be applied to checkbox and unimplemented validation types
                return;
            }
            if (fieldDef.AssignmentProp || this.AssignmentBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                if (this.AssignmentBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                    this.AssignmentBaseRequiredFieldDefs.push(fieldDef);
                    this.AssignmentBaseRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
                }
                if (this.AssignmentRequiredFields.indexOf(fieldDef.ID) === -1) {
                    this.AssignmentRequiredFields.push(fieldDef.ID);
                    this.AssignmentRequiredFieldDefs.push(fieldDef);
                    this.AssignmentRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
                }
            }
        }
    }

    public setFieldDefsForStories(fieldDef: FieldDef) {
        const validation = fieldDef.Validation.toUpperCase();
        if (fieldDef.Required) {
            if (validation === 'CHECKBOX' || (validation === 'LOOKUPLIST' && fieldDef.ID !== PSTFieldNames.PSTTags) || !this.globalTablesService.isValidationTypeImplemented(validation)) {
                // Required could not be applied to checkbox and unimplemented validation types
                return;
            }
            if (fieldDef.StoryProp || this.StoryBaseRequiredFields.indexOf(fieldDef.ID) > -1) {
                this.StoryRequiredFieldDefs.push(fieldDef);
                this.StoryRequiredFieldDefsDict[fieldDef.ID] = fieldDef;
            }
        }
        // if (fieldDef.StoryProp) {
        //     this.calculateStoryPropComparators(fieldDef);
        // }
    }
    // TODO: Figure this our in store
    private fillGroupSortIfEmpty() {
        // this.sortBySearch = this.sortByTopics.filter(item => this.sortByAssignments.some(item2 => item.id === item2.id));
    }

    public getFiltersForSearch(): (item: FieldDef) => boolean {
        return (item: FieldDef) => {
            if (item.Briefable && (item.TopicProp === true || item.StoryProp === true || item.AssignmentProp === true || item.RundownProp === true)) {
                return true;
            }
            return false;
        };
    }

    public getFiltersForTopicOrAssignmentOrScript(type: number): (item: FieldDef) => boolean {
        let t = '';
        if (type === ViewStateTypes.TOPIC) {
            t = 'Topic';
        }
        if (type === ViewStateTypes.ASSIGNMENT) {
            t = 'Assignment';
        }
        if (type === ViewStateTypes.SCRIPT) {
            t = 'Script';
        }
        return (item: FieldDef) => {
            if (item.Briefable && ((type === ViewStateTypes.SCRIPT && this.isScriptProperty(item)) || item[`${t}Prop`] === true)) {
                return true;
            }
            return false;
        };
    }

    private async copyToClipboardRenderer(data: ClipboardData, type: ClipboardTypes): Promise<string> {
        let selectedTemplateSource: { [id: string]: StorytellingExportFormatRecord } = null;
        if (this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[type] > 1) {
            const modalRes = await this.dialogService
                .open<{ [id: string]: StorytellingExportFormatRecord }>(ClipboardSourceChooserComponent, {
                    hasBackdrop: true,
                    data: {
                        type,
                    },
                })
                .closed.toPromise();
            if (modalRes) {
                selectedTemplateSource = modalRes;
            }
        } else if (this.nomWebServicesJson.HelloResults.PersonalExportFormatTemplates[type]) {
            selectedTemplateSource = this.nomWebServicesJson.HelloResults.PersonalExportFormatTemplates;
        } else if (this.nomWebServicesJson.HelloResults.TeamClipboardTemplates[type]) {
            selectedTemplateSource = this.nomWebServicesJson.HelloResults.TeamExportFormatTemplates;
        } else {
            selectedTemplateSource = this.nomWebServicesJson.HelloResults.EnterpriseExportFormatTemplates;
        }
        switch (type) {
            case ClipboardTypes.Topic:
            case ClipboardTypes.Assignment:
            case ClipboardTypes.Story: {
                if (selectedTemplateSource) {
                    const templateStr = selectedTemplateSource[type].PSTExportFormatTemplate;
                    if (templateStr) {
                        const template = Handlebars.compile(templateStr);
                        return template(data as StorytellingRecord, {
                            allowProtoMethodsByDefault: true,
                        });
                    }
                    return '';
                }
                break;
            }
            case ClipboardTypes.TopicView: {
                if (selectedTemplateSource) {
                    const templateStr = selectedTemplateSource[type].PSTExportFormatTemplate;
                    if (templateStr) {
                        const template = Handlebars.compile(templateStr);
                        const inputData = {
                            topics: data.topics,
                        };
                        return template(inputData, {
                            allowProtoMethodsByDefault: true,
                        });
                    }
                    return '';
                }
                break;
            }
            case ClipboardTypes.StoryView: {
                if (selectedTemplateSource) {
                    const templateStr = selectedTemplateSource[type].PSTExportFormatTemplate;
                    if (templateStr) {
                        const template = Handlebars.compile(templateStr);
                        const inputData = {
                            stories: data.stories,
                        };
                        return template(inputData, {
                            allowProtoMethodsByDefault: true,
                        });
                    }
                    return '';
                }
                break;
            }
            case ClipboardTypes.AssignmentView: {
                if (selectedTemplateSource) {
                    const templateStr = selectedTemplateSource[type].PSTExportFormatTemplate;
                    if (templateStr) {
                        const template = Handlebars.compile(templateStr);
                        const inputData = {
                            assignments: data.assignments,
                        };
                        return template(inputData);
                    }
                    return '';
                }
                break;
            }
            case ClipboardTypes.SearchView: {
                if (selectedTemplateSource) {
                    const templateStr = selectedTemplateSource[type].PSTExportFormatTemplate;
                    if (templateStr) {
                        const template = Handlebars.compile(templateStr);
                        const inputData = {
                            topics: data.topics,
                            assignments: data.assignments,
                            stories: data.stories,
                        };
                        return template(inputData, {
                            allowProtoMethodsByDefault: true,
                        });
                    }
                    return '';
                }
                break;
            }
            case ClipboardTypes.TopicUrl: {
                return `${location.origin}${location.pathname}#/PST/playbook/system/item/${(<StorytellingRecord>data).Guid}`;
            }
            case ClipboardTypes.AssignmentUrl: {
                return `${location.origin}${location.pathname}#/PST/playbook/assignment/${(<StorytellingRecord>data).Database}/${encodeURIComponent((<StorytellingRecord>data).Path.fullPath)}/${
                    (<StorytellingRecord>data).Guid
                }`;
            }
            default:
                throw `copyToClipboardRenderer() not implemented for playbook clipboard type ${type}`;
        }
    }

    public get playbookSearchResults(): PlaybookSearchResults {
        return DataStore.getSearchResults<PlaybookSearchResults>('playbook');
    }

    public async loadPlaybookObjects(vs?: ViewStateRecordsAlias, guid?: string, database?: NOMDatabase) {
        return this.playbookSearchService.getPlaybookResults(vs, guid, database);
    }

    public async loadRundowns(vs?: ViewStateRecordsAlias, database?: NOMDatabase) {
        return this.playbookSearchService.getRundownResults(vs, database);
    }

    public async loadTopicTemplates(vs: ViewStateRecordsAlias) {
        const templates: Array<StorytellingTopicRecord> = [];
        await this.appService.ngZone.runOutsideAngular(async () => {
            let loadPersonalTopicTemplates: boolean = true;
            const playbookTeam = this.globalTablesService.PlaybookTeamsArray.find(x => x.ID === this.appService.nomWebServicesJson.HelloResults.HomeProgram);
            let groupTemplatesPath = playbookTeam ? playbookTeam.ID : this.globalTablesService.DefaultPlaybookTeam.ID;
            const personalTemplatesPath = `U_${this.nomWebServicesJson.UserId}\\L`;

            if (this.navigationService.inEditTeamSettingsMode && this.navigationService.currentPlaybookTeam && this.navigationService.currentPlaybookTeam !== playbookTeam.ID) {
                groupTemplatesPath = this.navigationService.currentPlaybookTeam;
                loadPersonalTopicTemplates = false;
            }
            const resTemplates: ServiceResponse<StorytellingTopicRecord[]> = await this.playbookSearchService.getTemplateSearchResults(vs, groupTemplatesPath);
            if (resTemplates.status) {
                if (loadPersonalTopicTemplates) {
                    // do not load personal templates if different from the home team is selected
                    const personalTopicTemplates = resTemplates.data.filter(t => t.Path.fullPath === `U_${this.nomWebServicesJson.UserId}\\L`);
                    templates.push(...personalTopicTemplates);
                }
                const groupTopicTemplates = resTemplates.data.filter(t => t.Path.fullPath !== personalTemplatesPath);
                templates.push(...groupTopicTemplates);
            }
        });
        return templates;
    }

    private blankStoryTemplate = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT);
    public async loadStoryTemplates(vs: ViewStateRecordsAlias) {
        this.blankStoryTemplate.Title = this.languageSettings.PSTWebBlankOption;
        this.blankStoryTemplate.Guid = OPTION_BLANK;

        const templates: Array<StorytellingStoryRecord> = [this.blankStoryTemplate];
        await this.appService.ngZone.runOutsideAngular(async () => {
            let loadPersonalStoryTemplates: boolean = true;
            const templateFolder = 'X';
            const playbookTeam = this.globalTablesService.PlaybookTeamsArray.find(x => x.ID === this.appService.nomWebServicesJson.HelloResults.HomeProgram);
            let groupTemplatesPath = playbookTeam ? playbookTeam.ID : this.globalTablesService.DefaultPlaybookTeam.ID;

            if (this.navigationService.inEditTeamSettingsMode && this.navigationService.currentPlaybookTeam && this.navigationService.currentPlaybookTeam !== playbookTeam.ID) {
                groupTemplatesPath = this.navigationService.currentPlaybookTeam;
                loadPersonalStoryTemplates = false;
            }
            const personalTemplatesPath = `U_${this.nomWebServicesJson.UserId}\\L`;

            const resTemplates: ServiceResponse<StorytellingStoryRecord[]> = await this.playbookSearchService.getStoryTemplateSearchResults(vs, groupTemplatesPath);
            if (resTemplates.status) {
                if (loadPersonalStoryTemplates) {
                    // do not load personal templates if different from the home team is selected
                    const personalStoryTemplates = resTemplates.data.filter(t => t.Path.fullPath === `U_${this.nomWebServicesJson.UserId}\\${templateFolder}`);
                    templates.push(...personalStoryTemplates);
                }
                const groupStoryTemplates = resTemplates.data.filter(t => t.Path.fullPath !== personalTemplatesPath);
                templates.push(...groupStoryTemplates);
            }
        });
        return templates;
    }

    public async saveNewTopic(
        data: StorytellingTopicRecord,
        assignments: StorytellingAssignmentRecord[],
        stories: StorytellingStoryRecord[],
        additionalParams?: { [id: string]: any }
    ): Promise<ServiceResponse<StorytellingTopicRecord>> {
        const dataToSend = {};
        if (!data.PSTTopicStatus) {
            data.PSTTopicStatus = '';
        }
        data.Type = 137;

        for (const p in data) {
            if (data?.Guid) {
                if (
                    [
                        'MODTIME',
                        'MODBY',
                        'EXPIRES',
                        'CREATEDDATETIME',
                        'SOURCEGUID',
                        'GUID',
                        'PATH',
                        'PHYSICALPATH',
                        'VIRTUALPATH',
                        'ENPSDATABASE',
                        'CONTAINERTITLE',
                        'PRIORITY',
                        'APPROVED',
                        'OUTPUTTYPE',
                        'CREATOR',
                        'MOSOBJID',
                        'MOSCONTROL',
                        'OWNER',
                        'FILESTORAGEVERSION',
                        'DATABASE',
                    ].indexOf(p.toUpperCase()) >= 0
                )
                    continue;
            } else if (data?.Type === ObjectTypes.TOPIC) {
                if (['MODTIME'].indexOf(p.toUpperCase()) >= 0) continue;
            }

            const value = data[p];
            const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
            dataToSend[enpsField.key] = enpsField.value;
        }
        // add assignments to the dataToSend, if any
        const children = [];
        if (assignments && assignments.length > 0) {
            // process assignments
            for (let i = 0; i < assignments.length; i++) {
                const assignmentObject: any = {};
                for (const p in assignments[i]) {
                    if (
                        [
                            'MODTIME',
                            'MODBY',
                            'EXPIRES',
                            'CREATEDDATETIME',
                            'SOURCEGUID',
                            'GUID',
                            'PATH',
                            'PHYSICALPATH',
                            'VIRTUALPATH',
                            'ENPSDATABASE',
                            'CONTAINERTITLE',
                            'PRIORITY',
                            'APPROVED',
                            'OUTPUTTYPE',
                            'CREATOR',
                            'MOSOBJID',
                            'MOSCONTROL',
                            'OWNER',
                            'FILESTORAGEVERSION',
                            'DATABASE',
                        ].indexOf(p.toUpperCase()) >= 0
                    )
                        continue;
                    const value = assignments[i][p];
                    const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
                    assignmentObject[enpsField.key] = enpsField.value;
                }
                children.push(assignmentObject);
            }
        }

        const storiesToSend = [];
        if (stories && stories.length > 0) {
            // process assignments
            for (let i = 0; i < stories.length; i++) {
                const storyObject: any = {};
                for (const p in stories[i]) {
                    if (
                        [
                            'MODTIME',
                            'MODBY',
                            'EXPIRES',
                            'CREATEDDATETIME',
                            'SOURCEGUID',
                            'GUID',
                            'PATH',
                            'PHYSICALPATH',
                            'VIRTUALPATH',
                            'ENPSDATABASE',
                            'CONTAINERTITLE',
                            'PRIORITY',
                            'APPROVED',
                            'OUTPUTTYPE',
                            'CREATOR',
                            'MOSOBJID',
                            'MOSCONTROL',
                            'OWNER',
                            'FILESTORAGEVERSION',
                            'DATABASE',
                        ].indexOf(p.toUpperCase()) >= 0
                    )
                        continue;

                    const value = stories[i]?.[p];
                    const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);

                    if (enpsField.key?.toLowerCase() === PSTFieldNames.TextCommands.toLowerCase()) {
                        const textCommandKeys = Object.keys(enpsField.value);
                        const textCommandValues = Object.values(enpsField.value);
                        if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                            storyObject['TextCommandKeys'] = textCommandKeys;
                            storyObject['TextCommandValues'] = textCommandValues;
                        }
                    } else {
                        storyObject[enpsField.key] = enpsField.value;
                    }
                }
                storiesToSend.push(storyObject);
            }
        }

        const res = await this.topicService.createTopicContent(
            this.nomWebServicesJson.PSTServer,
            `P_${this.nomWebServicesJson.PSTGroup}\\W\\${this.nomWebServicesJson.PSTHoldFolder}`,
            null,
            null,
            false,
            dataToSend,
            children,
            storiesToSend,
            null,
            additionalParams
        );
        return res;
    }

    public initializeNewTopicRecordV2(record: StorytellingTopicRecord, viewState?: ViewStateRecord) {
        const defaultObjectPropertiesArr: Array<{ FieldName: string; FieldValue: any }> = [];
        // Initialize story owner
        if (!record.PSTTopicOwner) {
            record.PSTTopicOwner = new MultiValue<string>();
        }
        record.PSTTopicOwner.set(this.nomWebServicesJson.UserId);
        record.PSTTargetDate = new DateTime(new Date());

        // Initialize topic type
        let viewStateFilter = null;
        if (viewState) {
            viewStateFilter = (<TopicViewStateRecord>viewState).viewStateFilter.map;
        } else {
            viewStateFilter = (<TopicViewStateRecord>this._vs())?.viewStateFilter?.map;
        }

        const defaulTopicType = new MultiValue<string>();
        if (viewStateFilter[PSTFieldNames.PSTTopicType]) {
            for (const val of viewStateFilter[PSTFieldNames.PSTTopicType].Value) {
                if (val !== OPTION_ALL && val !== OPTION_BLANK) {
                    defaulTopicType.set(val);
                }
            }
            record.PSTTopicType = defaulTopicType;
        }

        for (const fieldID in this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC]) {
            const defaultValue = this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.TOPIC][fieldID];
            if (defaultValue && defaultValue.value) {
                if (defaultValue.validation.toUpperCase() === 'CHECKBOX') {
                    defaultValue.value = parseInt(defaultValue.value);
                }
                defaultObjectPropertiesArr.push({ FieldName: defaultValue.key, FieldValue: defaultValue.value });
            }
        }
        if (defaultObjectPropertiesArr.length > 0) {
            const defaultObjectProperties = new ObjectProperties(defaultObjectPropertiesArr);
            this.appService.nomWebServicesJson.convertor.deserializeV2(record, defaultObjectProperties);
        }
    }

    public initializeNewAssignmentRecordV2(assignmentRecord: StorytellingAssignmentRecord, topicRecord: StorytellingTopicRecord, assignmentType: string) {
        assignmentRecord.Title = topicRecord.Title;
        assignmentRecord.Description = '';
        assignmentRecord.PSTCoverageType = assignmentType;
        assignmentRecord.PSTTargetDate = new DateTime(new Date());

        const defaultObjectPropertiesArr: Array<{ FieldName: string; FieldValue: any }> = [];
        for (const fieldID in this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT]) {
            const defaultValue = this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.ASSIGNMENT][fieldID];
            if (defaultValue && defaultValue.value) {
                if (defaultValue.validation.toUpperCase() === 'CHECKBOX') {
                    defaultValue.value = parseInt(defaultValue.value);
                }
                defaultObjectPropertiesArr.push({ FieldName: defaultValue.key, FieldValue: defaultValue.value });
            }
        }
        if (defaultObjectPropertiesArr.length > 0) {
            const defaultObjectProperties = new ObjectProperties(defaultObjectPropertiesArr);
            this.appService.nomWebServicesJson.convertor.deserializeV2(assignmentRecord, defaultObjectProperties);
        }
        for (const fieldID in this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.ASSIGNMENT]) {
            if (topicRecord[fieldID]) {
                if (topicRecord[fieldID].deepCopy) {
                    assignmentRecord[fieldID] = topicRecord[fieldID].deepCopy();
                } else {
                    assignmentRecord[fieldID] = topicRecord[fieldID];
                }
            }
        }
        this.fieldMappingService.copyToChildV2(topicRecord, assignmentRecord);
    }

    public genenaretNewStory(topicData: StorytellingTopicRecord, type?: StoryOutputType): StorytellingStoryRecord {
        const newStory = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT);
        this.initializeNewStoryRecordV2(newStory, topicData, type);
        return newStory;
    }

    public initializeNewStoryRecordV2(storyRecord: StorytellingStoryRecord, topicRecord: StorytellingTopicRecord = null, storyOutputType: StoryOutputType = StoryOutputType.BROADCAST) {
        // TODO: check if this is needed
        // storyRecord.set(PSTFieldNames.Title, storyRecord.Title);
        storyRecord.Title = topicRecord?.Title || this.languageSettings.PSTStoryDefaultTitle;
        storyRecord.Slug = storyRecord.Title;
        if (!storyRecord.PSTStoryOwner) {
            storyRecord.PSTStoryOwner = new MultiValue<string>();
        }
        storyRecord.PSTStoryOwner.set(this.nomWebServicesJson.UserId);
        storyRecord.PSTTargetDate = new DateTime(new Date());
        if (storyRecord.Text == null) {
            storyRecord.Text = '';
            storyRecord.text = storyRecord.Text;
        }

        const defaultObjectPropertiesArr: Array<{ FieldName: string; FieldValue: any }> = [];
        for (const fieldID in this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.SCRIPT]) {
            const defaultValue = this.globalTablesService.objectDefaultValueFieldCache[ObjectTypes.SCRIPT][fieldID];
            if (defaultValue && defaultValue.value) {
                if (defaultValue.validation.toUpperCase() === 'CHECKBOX') {
                    defaultValue.value = parseInt(defaultValue.value);
                }
                defaultObjectPropertiesArr.push({ FieldName: defaultValue.key, FieldValue: defaultValue.value });
            }
        }
        if (defaultObjectPropertiesArr.length > 0) {
            const defaultObjectProperties = new ObjectProperties(defaultObjectPropertiesArr);
            this.appService.nomWebServicesJson.convertor.deserializeV2(storyRecord, defaultObjectProperties);
        }
        if (topicRecord) {
            for (const fieldID in this.globalTablesService.objectCopyFromParentFieldCache[ObjectTypes.SCRIPT]) {
                if (topicRecord?.[fieldID]) {
                    if (topicRecord?.[fieldID].deepCopy) {
                        storyRecord[fieldID] = topicRecord?.[fieldID]?.deepCopy() || null;
                    } else {
                        storyRecord[fieldID] = topicRecord?.[fieldID];
                    }
                }
            }
            this.fieldMappingService.copyToChildV2(topicRecord, storyRecord);
        }
        if (!storyRecord.PSTStoryOutputType) {
            storyRecord.PSTStoryOutputType = storyOutputType;
        }
    }

    public hasMultipleOwners(assignment: StorytellingAssignmentRecord): boolean {
        return !!assignment.PSTAssignmentOwner.length;
    }

    public serializeDataV2(data: StorytellingRecord): { [id: string]: any } {
        const serializedData = {};
        for (const p in data) {
            const value = data?.[p] || null;
            const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
            serializedData[enpsField.key] = enpsField.value;
        }
        return serializedData;
    }

    public async duplicateItem(linkToParent: boolean, oldRecord: StorytellingRecord, _isNew?: boolean, _mobile?: boolean): Promise<ServiceResponse<StorytellingTopicRecord>> {
        const { Database } = oldRecord;
        const { fullPath } = oldRecord.Path;
        const { groupId } = oldRecord.Path;
        const { Guid } = oldRecord;
        const res = await this.topicService.createTopicContent(Database, fullPath, Guid, groupId, linkToParent, null, null, null, null);
        return res;
    }

    public async duplicateAssignment(assignment: StorytellingAssignmentRecord, linkToParent: boolean) {
        const dataToSend = this.serializeDataV2(assignment);
        if (dataToSend[PSTFieldNames.Relationships]) {
            delete dataToSend[PSTFieldNames.Relationships];
        }
        Util.removeGeneratedFields(dataToSend, this.globalTablesService.FieldDefsDict);
        for (const f in dataToSend) {
            if (this.AssignmentDoNotCopyFieldDefsDict[f]) {
                delete dataToSend[f];
            }
        }
        const res = await this.topicService.insertIntoTopicContentV2(assignment.Database, assignment.Path.parentCollectionPath.fullPath, assignment.ParentGuid, '', dataToSend, true, '', linkToParent);
        return res;
    }

    public async createNewAssignment(
        database: string,
        path: string,
        type: string,
        topicData: StorytellingTopicRecord,
        assignmentRecord?: StorytellingAssignmentRecord
    ): Promise<ServiceResponse<StorytellingAssignmentRecord>> {
        const dataToSend = {};
        let data = assignmentRecord;
        if (!data) {
            data = initializeRecord<StorytellingAssignmentRecord>(ObjectTypes.ASSIGNMENT);
            await this.initializeNewAssignmentRecordV2(data, topicData, <AssignmentCoverageType>type);
        }

        for (const p in data) {
            const value = data[p];
            const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
            dataToSend[enpsField.key] = enpsField.value;
        }

        dataToSend[PSTFieldNames.Type] = ObjectTypes.ASSIGNMENT;
        dataToSend['CreatedBy'] = `CreatedBy.${this.nomWebServicesJson.UserId}`;
        const res = await this.topicService.insertIntoTopicContentV2(database, topicData.Path.fullPath, topicData.Guid, '', dataToSend, true, '', true);
        return res;
    }

    public genenaretNewAssignment(topicData: StorytellingTopicRecord, type: string): StorytellingAssignmentRecord {
        const newAssignment = initializeRecord<StorytellingAssignmentRecord>(ObjectTypes.ASSIGNMENT);
        this.initializeNewAssignmentRecordV2(newAssignment, topicData, <AssignmentCoverageType>type);
        return newAssignment;
    }

    public async createNewAssignmentV2(database: string, path: string, type: string, topicData: StorytellingTopicRecord): Promise<ServiceResponse<StorytellingAssignmentRecord>> {
        const newAssignment = this.genenaretNewAssignment(topicData, type);

        let dataToSend = {};
        for (const p in newAssignment) {
            const value = newAssignment[p];
            const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
            dataToSend[enpsField.key] = enpsField.value;
        }
        dataToSend[PSTFieldNames.Type] = ObjectTypes.ASSIGNMENT;
        dataToSend['CreatedBy'] = `CreatedBy.${this.nomWebServicesJson.UserId}`;

        dataToSend = cleanupRecord(dataToSend);
        const res = await this.topicService.insertIntoTopicContentV2<StorytellingAssignmentRecord>(database, topicData.Path.fullPath, topicData.Guid, '', dataToSend, true, '', true);
        return res;
    }

    public async createNewStory(database: string, topicData: StorytellingTopicRecord, generatedText?: string, promptTitle?: string): Promise<ServiceResponse<StorytellingStoryRecord>> {
        let dataToSend = {};
        // TODO: uncomment and rework when backfields are added
        // const data = storyRecord;
        // if (!data) {
        //     data = new StoryRecord();
        //     await this.initializeNewStoryRecord(data, topicData);
        // }

        // for (const p of data.keys) {
        //     const value = data.get(p);
        //     const enpsField = this.nomWebServicesJson.convertor.serializeField(value, p);
        //     dataToSend[enpsField.key] = enpsField.value;
        // }
        dataToSend[PSTFieldNames.Title] = promptTitle;
        dataToSend['Text'] = generatedText;
        dataToSend[PSTFieldNames.Type] = ObjectTypes.SCRIPT;
        dataToSend['CreatedBy'] = `CreatedBy.${this.nomWebServicesJson.UserId}`;
        dataToSend = cleanupRecord(dataToSend);
        const res = await this.topicService.insertIntoTopicContentV2<StorytellingStoryRecord>(database, topicData.Path.fullPath, topicData.Guid, '', dataToSend, true, '', true);
        return res;
    }

    public async createNewStoryFromTemplate(
        database: string,
        topic: StorytellingTopicRecord,
        templateProgramId: string,
        templateGuids: Array<string>
    ): Promise<ServiceResponse<Array<StorytellingStoryRecord>>> {
        let dataToSend = {};
        dataToSend[PSTFieldNames.Title] = topic.Title;
        dataToSend[PSTFieldNames.Type] = ObjectTypes.SCRIPT;
        dataToSend[PSTFieldNames.TargetPublicationDate] = new Date();
        dataToSend['CreatedBy'] = `CreatedBy.${this.nomWebServicesJson.UserId}`;

        dataToSend = cleanupRecord(dataToSend);
        const res = await this.topicService.insertTemplateIntoTopicContent(database, topic.Path.fullPath, topic.Guid, dataToSend, true, templateProgramId, templateGuids);
        return res;
    }

    public async copyENPSObject(sourceListData: ListData): Promise<ServiceResponse<StorytellingTopicRecord>> {
        const destinationDatabase: string = this.nomWebServicesJson.PSTServer;
        const destinationPath: string = `P_${this.nomWebServicesJson.PSTGroup}\\W\\${this.nomWebServicesJson.PSTHoldFolder}`;

        const propertiesOverride = [
            {
                FieldName: PSTFieldNames.TargetPublicationDate,
                FieldValue: new Date(),
            },
            {
                FieldName: PSTFieldNames.PSTCreatedBy,
                FieldValue: `CreatedBy.${sourceListData.Creator}`,
            },
        ];

        const res = await this.appService.nomWebServicesJson.copyENPSObject(
            sourceListData.ENPSDatabase,
            sourceListData.Path,
            sourceListData.Guid,
            sourceListData.Type,
            destinationDatabase,
            destinationPath,
            ObjectTypes.TOPIC,
            propertiesOverride
        );
        return res;
    }

    public async copyScriptToCollection(sourceListData: ListData, targetCollection: StorytellingTopicRecord) {
        const collectionDatabase: string = this.nomWebServicesJson.PSTServer;
        const collectionPath: string = targetCollection.Path.fullPath;
        const collectionGuid: string = targetCollection.Guid;
        const destinationType: ObjectTypes = ObjectTypes.ASSIGNMENT;

        const propertiesOverride: Array<{ FieldName: string; FieldValue: any }> = [];
        propertiesOverride.push({ FieldName: PSTFieldNames.DescriptionType, FieldValue: 'Text' });

        const res = await this.appService.nomWebServicesJson.copyScriptToCollection(
            collectionDatabase,
            collectionPath,
            collectionGuid,
            destinationType,
            sourceListData.ENPSDatabase,
            sourceListData.Path,
            sourceListData.Guid,
            sourceListData.Type,
            propertiesOverride
        );
        return res;
    }

    private _topicPropsCache: Array<FieldDef> = [];
    public getTopicFieldsList(): Array<FieldDef> {
        if (this._topicPropsCache && this._topicPropsCache.length > 0) return this._topicPropsCache;
        const groups = this.globalTablesService.getFieldGroupsFor('Topic');
        let ret = new Array<FieldDef>();
        for (const group of groups) {
            ret = [...ret, ...this.globalTablesService.getDynamicPropertiesFor('Topic', group)];
        }
        this._topicPropsCache = ret;
        return this._topicPropsCache;
    }

    public getRelationshipFilters(type: number): Array<FilterDef> {
        if (type !== ViewStateTypes.TOPIC) {
            return new Array<FilterDef>();
        }
        const filters: Array<FilterDef> = [];
        const assignmentsFilter = new FilterDef();
        assignmentsFilter.ID = 'Assignments';
        assignmentsFilter.IsRelationship = true;
        assignmentsFilter.PSTField = PSTFieldNames.DescriptionType;
        assignmentsFilter.DefaultCaption = this.languageSettings.PSTWebAssignmentsFilterLabel;
        assignmentsFilter.Validation = 'CHECKLIST';
        assignmentsFilter.Options = [
            { id: OPTION_ALL, title: this.languageSettings.PSTWebNotAppliedOption, selectable: true },
            { id: OPTION_ANY, title: this.languageSettings.PSTWebAnyOption, selectable: true },
            { id: OPTION_BLANK, title: this.languageSettings.PSTWebBlankOption, selectable: true },
        ].concat(this.globalTablesService.getListItemsForField(PSTFieldNames.DescriptionType).map(item => ({ id: item.id, title: this.getTitleForAssignmentCoverage(item.id), selectable: true })));
        assignmentsFilter.init();
        const missingAssignmentsFilter = new FilterDef();
        missingAssignmentsFilter.IsRelationship = true;
        missingAssignmentsFilter.ID = 'MissingAssignments';
        missingAssignmentsFilter.PSTField = PSTFieldNames.DescriptionType;
        missingAssignmentsFilter.IsNegation = true;
        missingAssignmentsFilter.DefaultCaption = this.languageSettings.PSTWebMissingAssignmentsFilterLabel;
        missingAssignmentsFilter.Validation = 'CHECKLIST';
        missingAssignmentsFilter.Options = [{ id: OPTION_ALL, title: this.languageSettings.PSTWebNotAppliedOption, selectable: true }].concat(
            this.globalTablesService.getListItemsForField(PSTFieldNames.DescriptionType).map(item => ({ id: item.id, title: this.getTitleForAssignmentCoverage(item.id), selectable: true }))
        );
        missingAssignmentsFilter.init();
        filters.push(assignmentsFilter);
        filters.push(missingAssignmentsFilter);
        return filters;
    }

    private getTitleForAssignmentCoverage(id: string): string {
        switch (id) {
            case 'Text':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_Text;
            case 'Photo':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_Photo;
            case 'Video':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_Video;
            case 'LiveVideo':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_LiveVideo;
            case 'Audio':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_Audio;
            case 'Graphic':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_Graphic;
            case 'Interactive':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_Interactive;
            case 'SocialPromotion':
                return this.languageSettings.PSTWebListValue_PSTCoverageType_SocialPromotion;
            default:
                return id;
        }
    }

    public async initializeTopicFromTemplate(database: string, path: string, guid: string): Promise<StorytellingTopicRecord> {
        const fieldsToSkip: { [field: string]: boolean } = {};
        fieldsToSkip[PSTFieldNames.Title.toLowerCase()] = true;
        const rec = await this.topicService.getTopicTemplateContent(database, path, guid);
        const data = initializeRecord<StorytellingTopicRecord>(ObjectTypes.TOPIC);
        const dt = new DateTime();
        dt.set(new Date());
        rec.data[PSTFieldNames.TargetPublicationDate] = dt;
        rec.data.PSTTopicOwner = new MultiValue<string>();
        rec.data.PSTTopicOwner.set(this.nomWebServicesJson.UserId);
        copyDataAndChildrenTo(rec.data, data, false, true, fieldsToSkip);

        // Get template stories
        const storiesRes = await this.topicService.getTopicStories(database, path, guid);
        if (storiesRes?.data?.length) {
            data.stories = storiesRes.data;
        }
        return data;
    }

    public initializeTopicFromWire(wire: StorytellingWireRecord): StorytellingTopicRecord {
        const rec = initializeRecord<StorytellingTopicRecord>(ObjectTypes.TOPIC);
        const dt = new DateTime();
        dt.set(new Date());
        rec[PSTFieldNames.TargetPublicationDate] = dt;
        rec[PSTFieldNames.PSTTopicStoryOwner] = new MultiValue<string>();
        rec[PSTFieldNames.PSTTopicStoryOwner].set(this.nomWebServicesJson.UserId);
        rec[PSTFieldNames.Title] = rec[PSTFieldNames.Slug] = wire.Title;
        rec[PSTFieldNames.Description] = wire.Content;
        return rec;
    }

    public async initializeStoryFromTemplate(database: string, path: string, guid: string): Promise<StorytellingStoryRecord> {
        const rec = await this.basicContentService.getRecord<StorytellingStoryRecord>(database, path, guid);
        const data = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT);
        const dt = new DateTime();
        dt.set(new Date());
        // TODO: change TargetPublicationDate to something that story uses
        rec.data[PSTFieldNames.TargetPublicationDate] = dt;
        rec.data.PSTStoryOwner = new MultiValue<string>();
        rec.data.PSTStoryOwner.set(this.nomWebServicesJson.UserId);
        copyDataTo(rec.data, data);

        // Set default values for Text property. Text is the property that is used in the UI to display the story's content.
        // It is a required property and should always have a value.
        // If the template story does not have a value for Text, set it to an empty string.
        if (data.Text == null) {
            data.Text = '';
            data.text = '';
        }
        return data;
    }
}
